import api from '@/utils/api';

/**
 * Lightweight reverse-engineering / tamper monitor.
 *
 * Detects (heuristically, without blocking the user):
 *  - Inspection shortcuts: F12, Ctrl/Cmd+Shift+I/J/C, Ctrl/Cmd+U (view-source)
 *  - DevTools being open (window-size delta + a console getter trip)
 *
 * Each distinct signal is reported to the backend at most once per browser
 * session (the backend throttles the admin email to once per 24h per user).
 * This is intentionally non-hostile: we observe and alert, we never break the
 * page or trap the user.
 */

const reported = new Set();

function report(type, details, severity = 'medium') {
  if (reported.has(type)) return;
  const sessionKey = `sec_reported_${type}`;
  try {
    if (sessionStorage.getItem(sessionKey)) {
      reported.add(type);
      return;
    }
    sessionStorage.setItem(sessionKey, '1');
  } catch {
    /* sessionStorage may be unavailable */
  }
  reported.add(type);

  // Fire-and-forget; never surface errors to the user.
  api.post('/security/report', {
    type,
    severity,
    details: String(details || '').slice(0, 500),
    url: window.location?.pathname || '',
    user_agent: navigator.userAgent || '',
  }).catch(() => {});
}

function onKeyDown(e) {
  const k = (e.key || '').toLowerCase();
  const ctrlOrMeta = e.ctrlKey || e.metaKey;

  if (k === 'f12') {
    report('source_inspection', 'F12 pressed');
    return;
  }
  if (ctrlOrMeta && e.shiftKey && (k === 'i' || k === 'j' || k === 'c')) {
    report('source_inspection', `DevTools shortcut Ctrl+Shift+${k.toUpperCase()}`);
    return;
  }
  if (ctrlOrMeta && k === 'u') {
    report('source_inspection', 'View-source shortcut Ctrl+U');
  }
}

function checkDevtoolsOpen() {
  const threshold = 170;
  const widthGap = window.outerWidth - window.innerWidth > threshold;
  const heightGap = window.outerHeight - window.innerHeight > threshold;
  if (widthGap || heightGap) {
    report('devtools_open', `Viewport gap detected (w:${window.outerWidth - window.innerWidth}, h:${window.outerHeight - window.innerHeight})`, 'high');
    return;
  }

  // Console getter trip: if DevTools is open & inspecting, the getter fires.
  let tripped = false;
  const bait = /./;
  bait.toString = function () {
    tripped = true;
    return 'safer-i';
  };
   
  console.debug('%c', bait);
  if (tripped) {
    report('devtools_open', 'Console inspection detected', 'high');
  }
}

let started = false;
let intervalId = null;

export function initSecurityMonitor() {
  if (started) return;
  started = true;
  window.addEventListener('keydown', onKeyDown, true);
  // Periodic, low-frequency check (every 4s).
  intervalId = setInterval(checkDevtoolsOpen, 4000);
}

export function stopSecurityMonitor() {
  started = false;
  window.removeEventListener('keydown', onKeyDown, true);
  if (intervalId) clearInterval(intervalId);
}
